import {BaseGraphNode} from "#graph/nodes/base/base-graph-node"
import type {NodeParameters} from "#graph/nodes/base/types"
import {GraphNode} from "#graph/nodes/graph/graph-node"
import {z} from "zod/v4-mini"

export type Evaluated<T, Context = unknown> = T extends GraphNode<infer Returns, Context> ? Returns : T

export const isGraphNodeInstance = (value: unknown): value is GraphNode => {
    return value instanceof GraphNode
}

export declare abstract class Class {
    constructor(..._args: any[])
}

// graphNode(SomeClass): `z.instanceof(SomeClass)` — works for any class; for ABSTRACT base classes use this form.
// graphNode<X>("X"): string identity via `nodeClass` — for CONCRETE classes, cycle-safe (only needs `import type`).
export function graphNode<NodeType extends typeof Class>(node: NodeType): z.ZodMiniType<InstanceType<NodeType>, InstanceType<NodeType>>
export function graphNode<T extends BaseGraphNode>(nodeClassName: string): z.ZodMiniType<T, T>
export function graphNode(arg: string | typeof Class): z.ZodMiniType<unknown, unknown> {
    return typeof arg === "string"
        ? (z.custom((v) => v instanceof BaseGraphNode && v.nodeClass === arg) as z.ZodMiniType<unknown, unknown>)
        : (z.instanceof(arg) as z.ZodMiniType<unknown, unknown>)
}

// Runtime predicate variant of graphNode — same dual signature.
export function isNodeClass<NodeType extends typeof Class>(node: NodeType): (v: unknown) => v is InstanceType<NodeType>
export function isNodeClass<T extends BaseGraphNode>(nodeClassName: string): (v: unknown) => v is T
export function isNodeClass(arg: string | typeof Class): (v: unknown) => boolean {
    return typeof arg === "string" ? (v: unknown) => v instanceof BaseGraphNode && v.nodeClass === arg : (v: unknown) => v instanceof arg
}

export const parameterValue = <ValueType, Context>(
    schema: z.ZodMiniType<ValueType>,
    nodeType: typeof GraphNode<ValueType, Context> = GraphNode<ValueType, Context>,
) => {
    return z.union([schema, graphNode(nodeType)]) as z.ZodMiniType<ParameterValue<ValueType, Context>, ParameterValue<ValueType, Context>>
}
export type ParameterValue<Value, Context = any, Parameters extends NodeParameters = NodeParameters> = Value | GraphNode<Value, Context, Parameters>
